import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PollContent from './PollContent';
import AddPoll from './AddPoll';
import { getSocket } from '../../../../../socket';

export default function PollPage({ agenda, item, meeting, onPollClosed }) {
  // const [polls, setPolls] = useState([]); // State to store polls
  const [matchingPoll, setMatchingPoll] = useState(null);
  const params = useParams();
  const effectiveMeetingId = String(params.meetingId || meeting?.meetingDetails?.id || meeting?._id || item?.meeting || '').trim();
  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector((state) => state.auth);
  const specialAssignments = meeting?.meetingDetails?.specialAssignments || meeting?.specialAssignments || {}
  const authIdentityIds = [
    authUser?.user?.localUserId,
    authUser?.user?._id,
    authUser?.user?.id,
    authUser?.user?.tmAuthUserId,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const isTimerManager = Boolean(
    authIdentityIds.length
    && authIdentityIds.includes(String(specialAssignments.timerManager || '').trim())
  );
  const canManagePoll = isTimerManager;

  // console.log(meetingId);

  const getPolls = useCallback(async () => {
    if (!effectiveMeetingId) return;
    try {
      const response = await fetch(`${url}/polls/${effectiveMeetingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        // setPolls(data); // Store all poll objects
        // Find poll with the same title as agendaEvent.title
        const found = data.find(
          poll => poll.title === item?.agendaEvent?.name
        );
        setMatchingPoll(found || null);
      } else {
        console.error('Failed to fetch polls:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching polls:', error);
    }
  }, [url, effectiveMeetingId, item?.agendaEvent?.name, authUser?.token]);

  useEffect(() => {
    getPolls();
  }, [getPolls]);

  useEffect(() => {
    if (!effectiveMeetingId) return undefined;

    const socket = getSocket();
    socket.emit('meeting:join', effectiveMeetingId);

    const handlePollStatusChanged = (payload) => {
      if (payload && String(payload.meetingId || '') !== effectiveMeetingId) return;
      getPolls();
    };

    socket.on('poll:status-changed', handlePollStatusChanged);

    return () => {
      socket.off('poll:status-changed', handlePollStatusChanged);
    };
  }, [effectiveMeetingId, getPolls]);

  useEffect(() => {
    const handlePollCreated = (event) => {
      const createdMeetingId = String(event?.detail?.meetingId || '').trim();
      const currentMeetingId = effectiveMeetingId;
      const createdTitle = String(event?.detail?.title || '').trim();
      const currentTitle = String(item?.agendaEvent?.name || '').trim();

      if (!createdMeetingId || !currentMeetingId || createdMeetingId !== currentMeetingId) {
        return;
      }

      if (!createdTitle || !currentTitle || createdTitle !== currentTitle) {
        return;
      }

      getPolls();
    };

    window.addEventListener('meeting:poll-created', handlePollCreated);

    return () => {
      window.removeEventListener('meeting:poll-created', handlePollCreated);
    };
  }, [getPolls, item?.agendaEvent?.name, effectiveMeetingId]);

  useEffect(() => {
    const handlePollDeleted = (event) => {
      const deletedMeetingId = String(event?.detail?.meetingId || '').trim();
      const currentMeetingId = effectiveMeetingId;
      const deletedTitle = String(event?.detail?.title || '').trim();
      const currentTitle = String(item?.agendaEvent?.name || '').trim();
      const deletedPollId = String(event?.detail?.pollId || '').trim();
      const currentPollId = String(matchingPoll?._id || '').trim();

      if (!deletedMeetingId || !currentMeetingId || deletedMeetingId !== currentMeetingId) {
        return;
      }

      const sameTitle = Boolean(deletedTitle && currentTitle && deletedTitle === currentTitle);
      const samePoll = Boolean(deletedPollId && currentPollId && deletedPollId === currentPollId);

      if (!sameTitle && !samePoll) {
        return;
      }

      getPolls();
    };

    window.addEventListener('meeting:poll-deleted', handlePollDeleted);

    return () => {
      window.removeEventListener('meeting:poll-deleted', handlePollDeleted);
    };
  }, [getPolls, item?.agendaEvent?.name, matchingPoll?._id, effectiveMeetingId]);

  return (
    <div className="poll-page-content">
      {canManagePoll && !matchingPoll &&
        <AddPoll agenda={agenda} item={item} meetingId={effectiveMeetingId} getPolls={getPolls} />
      }
      {matchingPoll ? (
        <PollContent pollId={matchingPoll._id} getPolls={getPolls} onPollClosed={onPollClosed} isPollManager={canManagePoll} />
      ) : (
        <p className="poll-page-empty">The Poll for this Agenda Item was not yet created.</p>
      )}
    </div>
  )
}